// Survey render + autosave diagnostic logging (card #4688, part 2).
//
// A Tasker reported completed work vanishing on re-open under flapping
// signal, and nothing in app_log said which copy of the survey rendered, how
// many answers it carried, or whether an autosave actually reached the
// cache. This module is the shared tail for the online (server) and offline
// (phone cache / borrowed sibling) render paths in result_editor_controller.js,
// so the two never drift into two different log shapes.
//
// COUNTS ONLY -- never the answer text or a photo's bytes/URL.

import { logEvent } from "./field_logger"

const PHOTO_TYPES = new Set(["file", "camera-upload-question", "camera-mult-upload-question"])

// Counts photo-type question elements anywhere in a survey definition,
// including inside panels. A slot -- present whether or not the Tasker has
// taken that photo yet -- not an answer count.
export function countPhotoSlots(surveyJson) {
  if (!surveyJson || !Array.isArray(surveyJson.pages)) return 0

  let count = 0
  const walk = (elements) => {
    if (!Array.isArray(elements)) return
    for (const element of elements) {
      if (PHOTO_TYPES.has(element.type)) count++
      if (element.type === "panel" && Array.isArray(element.elements)) walk(element.elements)
    }
  }
  surveyJson.pages.forEach(page => walk(page.elements))
  return count
}

// Number of answered questions -- a plain key count, never the values.
export function answerCount(resultJson) {
  return resultJson ? Object.keys(resultJson).length : 0
}

// Logs which copy of a response's survey rendered -- "server", "phone_cache",
// or "borrowed_sibling" -- with the answer count and photo slot count shown.
export async function logSurveyRendered({ responseId, missionId, source, surveyJson, resultJson }) {
  await logEvent("response", "Survey rendered", {
    responseId,
    missionId: missionId || null,
    source,
    answerCount:    answerCount(resultJson),
    photoSlotCount: countPhotoSlots(surveyJson)
  }).catch(() => {})
}

// Logs when the server's own answer count and this phone's cached draft
// disagree at load time -- counts only, never which answers differ.
export async function logAnswerCountMismatch(responseId, serverResultJson, phoneResultJson) {
  const serverCount = answerCount(serverResultJson)
  const phoneCount  = answerCount(phoneResultJson)
  if (serverCount === phoneCount) return

  await logEvent("response", "Server and phone answer counts differ", {
    responseId,
    serverAnswerCount: serverCount,
    phoneAnswerCount:  phoneCount
  }).catch(() => {})
}
